import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import UpcomingSingleContent from '../dashboard/UpcomingSingleContent'
import { deleteById, deleteContentSeries, getUpcomingContent } from '../../server/server'
import BigImageModal from './BigImageModal'
import ConfirmationModal from './ConfirmationModal'
import { notify } from '../../utils/util'
import i18n from '../../i18n'
import { ANALYTICS_EVENTS } from '../../analytics/events'
import useTrackedSurface from '../../analytics/useTrackedSurface'
import ContentListModal, { PANEL_TIMEOUT } from './ContentListModal/ContentListModal'
import usePagedContentList, { assertOk } from './ContentListModal/usePagedContentList'
import { getContentKey } from '../content/ToGo/ContentCard'

const SCROLL_ID = 'upcoming-content-scroll'
const fetchPage = (offset) => getUpcomingContent({ loadedContent: offset }).then(assertOk)

const UpcomingContentModal = memo(({ isOpen, onClose, count, onCountChanged }) => {
    const { t } = useTranslation()
    const history = useHistory()
    const list = usePagedContentList({ fetchPage, enabled: isOpen })
    const [imageModalOpen, setImageModalOpen] = useState(false)
    const [selectedImage, setSelectedImage] = useState('')
    const [deleteModalOpen, setDelModalOpen] = useState(false)
    const [selContent, setSelContent] = useState(null)
    const navTimer = useRef()
    useTrackedSurface(ANALYTICS_EVENTS.DASHBOARD_UPCOMING_CONTENT_VIEWED, {
        active: isOpen,
        content: 'Upcoming content',
    })

    useEffect(() => () => clearTimeout(navTimer.current), [])

    // Let the panel slide out before the route changes underneath it.
    const openPage = (page) => {
        onClose()
        clearTimeout(navTimer.current)
        navTimer.current = setTimeout(() => history.push(page), PANEL_TIMEOUT)
    }

    const openDeleteForSingle = useCallback((content) => {
        setSelContent(content)
        setDelModalOpen(true)
    }, [])
    const closeDelModal = useCallback(() => setDelModalOpen(false), [])

    const openBigImage = useCallback((url) => {
        setSelectedImage(url)
        setImageModalOpen(true)
    }, [])
    const closeImageModal = useCallback(() => {
        setImageModalOpen(false)
        setSelectedImage('')
    }, [])

    const finishDelete = (resp, successKey) => {
        closeDelModal()
        if (resp?.status === 200) {
            list.reload()
            onCountChanged?.()
            notify(t(successKey))
        } else {
            notify(t('Content is not deleted'))
        }
    }

    // One row can stand for a whole recurring series (series_count > 1). The
    // primary action removes just the next run; the secondary removes that run
    // and every other scheduled one.
    const handleDelete = async () => finishDelete(await deleteById({ collection: 'content', id: selContent?.id }), 'contentDeleted')
    const handleDeleteSeries = async () => finishDelete(await deleteContentSeries(selContent?.id), 'seriesDeleted')
    const isSeries = !!selContent?.repeat_id
    const seriesSub = isSeries ? `${selContent?.repeatPeriodText || ''} · ${t('scheduledRuns', { count: selContent?.series_count || 1 })}`.trim() : ''

    const renderRow = (content, index) => (
        <UpcomingSingleContent key={getContentKey(content, index)} content={content} index={index} onDelete={openDeleteForSingle} onOpenImage={openBigImage} />
    )

    const headerContent = (
        <div className="mt-6">
            <button
                type="button"
                className="w-full bg-whiteContrast flex items-center justify-center border border-mudPurple hover:border-deepPurple rounded-xl py-4 text-sm text-deepPurple font-semibold transition-colors"
                id="create-content-button"
                onClick={() => openPage('/content')}
            >
                {t('createYourOwnContent')}
            </button>
            <button
                type="button"
                className="mt-3 w-full bg-whiteContrast flex items-center justify-center border border-mudPurple hover:border-deepPurple rounded-xl py-4 text-sm text-deepPurple font-semibold transition-colors"
                id="run-ready-to-go-content-button"
                onClick={() => openPage('/readytogo')}
            >
                {t('runReadyToGoContent')}
            </button>
        </div>
    )

    return (
        <ContentListModal
            isOpen={isOpen}
            onClose={onClose}
            onAfterClose={list.reset}
            title={t('upcomingContent')}
            headerContent={headerContent}
            items={list.items}
            loading={list.loading}
            refreshing={list.refreshing}
            hasMore={list.hasMore}
            version={list.version}
            loadMore={list.loadMore}
            emptyText={t("You don't have any upcoming content")}
            renderRow={renderRow}
            scrollId={SCROLL_ID}
            closeButton={{ id: 'upcoming-content-modal-close', label: t('upcomingContent'), count }}
        >
            {isOpen && (
                <>
                    <ConfirmationModal
                        modalIsOpen={deleteModalOpen}
                        closeModal={closeDelModal}
                        confirmation={handleDelete}
                        confirmButtonText={isSeries ? 'deleteThisRunOnly' : 'OK'}
                        secondaryConfirmation={isSeries ? handleDeleteSeries : null}
                        secondaryButtonText="deleteAllScheduledRuns"
                        message={t('doYouWantToDeleteThis')}
                        subMessage={isSeries ? seriesSub : i18n.language === 'en' ? `Delete ${selContent?.type} content?` : ''}
                    />
                    <BigImageModal modalIsOpen={imageModalOpen} closeModal={closeImageModal} selectedImage={selectedImage} />
                </>
            )}
        </ContentListModal>
    )
})

export default UpcomingContentModal
